import { useState, createContext, useContext, useEffect, useMemo, useCallback } from "react";
import { Link, useLocation } from "wouter";
import {
  LayoutDashboard,
  Building2,
  Users,
  UserPlus,
  Calculator,
  Megaphone,
  FolderOpen,
  BarChart,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  CreditCard,
  UserCheck,
  Settings,
  User,
  Briefcase,
  DollarSign,
  Shield,
  MessageCircle,
  TrendingUp,
  AlertCircle,
  LucideIcon,
  ClipboardList,
  ShieldCheck,
  BookOpen,
  Brain,
  MapPin,
  ChevronDown,
  HeartHandshake,
  Wrench,
  GraduationCap,
  Plug,
  BarChart3,
  FlaskConical,
  ListChecks,
  Activity,
  Workflow as WorkflowIcon,
  Library,
  Send,
  Inbox,
  CalendarClock,
  HelpCircle,
  ScrollText,
} from "lucide-react";

// Megaphone is already imported at the top of the file
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useRole } from "@/hooks/useRole";
import { useQuery } from "@tanstack/react-query";
import { useProfileCompletion } from "@/hooks/useProfileCompletion";
import { usePermissions } from "@/hooks/usePermissions";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Navigation item type with optional permission requirements
interface NavItem {
  icon: LucideIcon;
  label: string;
  href: string;
  tourId?: string;
  // Permission requirements - user must have ANY of these permissions to see the item
  permissions?: string[];
}

// Optional grouping for sidebars that want collapsible sections (superadmin).
// Other portals keep their flat NavItem[] structure.
interface NavGroup {
  kind: "group";
  id: string;            // stable key for localStorage open-state
  icon: LucideIcon;
  label: string;
  items: NavItem[];
}
type NavEntry = NavItem | NavGroup;

function isNavGroup(e: NavEntry): e is NavGroup {
  return (e as NavGroup).kind === "group";
}

// Navigation items for different roles
const getBrokerNavigationItems = (): NavItem[] => {
  const items: NavItem[] = [
    { icon: LayoutDashboard, label: "Dashboard", href: "/", tourId: "sidebar-dashboard" },
    {
      icon: Building2,
      label: "Agency",
      href: "/agency",
      tourId: "sidebar-agency",
      permissions: [
        "agency:view",
        "agency:edit",
        "agency:members",
        "agency:roles",
        // Allow access for users who only have agency commissions permissions
        "agency_commissions:view",
        "agency_commissions:reports",
        "agency_commissions:manage",
      ],
    },
    { icon: Users, label: "Clients", href: "/clients", tourId: "sidebar-clients", permissions: ["clients:view"] },
    { icon: UserPlus, label: "Prospects", href: "/prospects", tourId: "sidebar-prospects", permissions: ["prospects:view"] },
  ];

  items.push(
    { icon: Calculator, label: "Quoting", href: "/quoting", tourId: "sidebar-quoting", permissions: ["quotes:view"] },
    { icon: Megaphone, label: "Marketing", href: "/marketing", tourId: "sidebar-marketing", permissions: ["marketing:view"] },
    { icon: FolderOpen, label: "Directory", href: "/directory", tourId: "sidebar-directory", permissions: ["directory:view"] },
    { icon: BarChart, label: "Reports", href: "/reports", tourId: "sidebar-reports", permissions: ["reports:view", "reports:financial"] },
  );

  return items;
};

const employerNavigationItems = [
  { icon: LayoutDashboard, label: "Dashboard", href: "/", tourId: "employer-sidebar-dashboard" },
  { icon: Building2, label: "Company Profile", href: "/company", tourId: "employer-sidebar-company" },
  { icon: Users, label: "Employees", href: "/employees", tourId: "employer-sidebar-employees" },
  { icon: DollarSign, label: "Payroll", href: "/payroll" },
  { icon: Clock, label: "Time Off", href: "/time-off", tourId: "employer-sidebar-timeoff" },
  { icon: CreditCard, label: "Benefits", href: "/benefits", tourId: "employer-sidebar-benefits" },
  { icon: Shield, label: "Compliance", href: "/compliance", tourId: "employer-sidebar-compliance" },
  { icon: ClipboardList, label: "ACA", href: "/aca" },
  { icon: Settings, label: "HR", href: "/hr" },
  { icon: FileText, label: "Documents", href: "/documents", tourId: "employer-sidebar-documents" },
  { icon: BarChart, label: "Reports", href: "/reports" },
];

// ── GA (General Agency) Portal navigation ──────────────────────────────────
const gaNavigationItems: NavItem[] = [
  { icon: LayoutDashboard, label: "Dashboard", href: "/ga/dashboard" },
  { icon: Users, label: "Broker Accounts", href: "/ga/brokers" },
  { icon: CreditCard, label: "License Pool", href: "/ga/license-pool" },
  { icon: Library, label: "Documents", href: "/ga/documents" },
  { icon: Send, label: "Case Routing", href: "/ga/api-connections" },
  { icon: Inbox, label: "Submissions", href: "/ga/submissions" },
  { icon: CalendarClock, label: "Renewal Calendar", href: "/ga/renewal-calendar" },
  { icon: HelpCircle, label: "Service Requests", href: "/ga/service-requests" },
  { icon: Building2, label: "Carrier Settings", href: "/ga/carrier-settings" },
  { icon: UserCheck, label: "Users", href: "/ga/users" },
  { icon: ScrollText, label: "Audit Log", href: "/ga/audit-log" },
  { icon: Settings, label: "Settings", href: "/ga/settings" },
];

const superadminNavigationEntries: NavEntry[] = [
  { icon: LayoutDashboard, label: "Dashboard", href: "/superadmin" },
  {
    kind: "group", id: "sales", label: "Sales", icon: TrendingUp,
    items: [
      { icon: UserPlus,   label: "Leads",         href: "/superadmin/leads" },
      { icon: TrendingUp, label: "Opportunities", href: "/superadmin/opportunities" },
      { icon: FileText,   label: "Proposals",     href: "/superadmin/proposals" },
    ],
  },
  {
    kind: "group", id: "clients", label: "Clients", icon: Building2,
    items: [
      { icon: Building2, label: "Clients", href: "/superadmin/clients" },
      { icon: GraduationCap, label: "Onboarding", href: "/superadmin/onboarding" },
    ],
  },
  {
    kind: "group", id: "marketing", label: "Marketing", icon: Megaphone,
    items: [
      { icon: Megaphone, label: "Campaigns", href: "/superadmin/marketing" },
    ],
  },
  {
    kind: "group", id: "workflows", label: "Workflows", icon: WorkflowIcon,
    items: [
      { icon: WorkflowIcon, label: "All workflows", href: "/superadmin/workflows" },
    ],
  },
  {
    kind: "group", id: "benefits-catalog", label: "Benefits Catalog", icon: ShieldCheck,
    items: [
      { icon: Shield,        label: "Carriers",     href: "/superadmin/carriers" },
      { icon: MapPin,        label: "States",       href: "/superadmin/states" },
      { icon: MapPin,        label: "Rate Areas",   href: "/superadmin/rate-areas" },
      { icon: FileText,      label: "Plan Library", href: "/superadmin/plan-library" },
      { icon: ClipboardList, label: "Form Library", href: "/superadmin/form-library" },
    ],
  },
  {
    kind: "group", id: "support", label: "Support", icon: HeartHandshake,
    items: [
      { icon: BookOpen,    label: "Knowledge Base", href: "/superadmin/knowledge-base" },
      { icon: Brain,       label: "AI Training",    href: "/superadmin/ai-training" },
      { icon: AlertCircle, label: "Bug Reports",    href: "/superadmin/bug-reports" },
      { icon: ListChecks,  label: "Test Coverage",  href: "/superadmin/test-coverage" },
    ],
  },
  {
    kind: "group", id: "integrations", label: "Integrations", icon: Plug,
    items: [
      { icon: Plug, label: "Vendor catalog", href: "/superadmin/integrations" },
    ],
  },
  {
    kind: "group", id: "reports", label: "Reports", icon: BarChart3,
    items: [
      { icon: BarChart3, label: "Feature analytics", href: "/superadmin/reports/feature-analytics" },
      { icon: Activity,  label: "Client health",     href: "/superadmin/reports/client-health" },
      { icon: FlaskConical, label: "AI evals",       href: "/superadmin/reports/ai-evals" },
    ],
  },
  {
    kind: "group", id: "system", label: "System", icon: Wrench,
    items: [
      { icon: Users,     label: "Users",    href: "/superadmin/users" },
      { icon: Building2, label: "Agencies", href: "/superadmin/agencies" },
    ],
  },
  { icon: Settings, label: "Settings", href: "/superadmin/settings" },
];

// Persisted open/closed state per group. Defaults to open when no entry exists.
function useGroupOpenState() {
  const KEY = "sidebar:superadmin:groups";
  const [state, setState] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) return JSON.parse(raw);
    } catch {}
    return {};
  });
  const isOpen = useCallback((id: string) => state[id] !== false, [state]);
  const toggle = useCallback((id: string) => {
    setState((prev) => {
      const next = { ...prev, [id]: !(prev[id] !== false) };
      try { localStorage.setItem(KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);
  return { isOpen, toggle };
}

const getEmployeeNavigationItems = (includeCommissions: boolean = false) => {
  const baseItems = [
    { icon: LayoutDashboard, label: "Dashboard", href: "/" },
    { icon: User, label: "Profile", href: "/profile" },
  ];
  
  if (includeCommissions) {
    baseItems.push({ icon: TrendingUp, label: "Commissions", href: "/commissions" });
  }
  
  return [
    ...baseItems,
    { icon: Briefcase, label: "Employment", href: "/employment" },
    { icon: CreditCard, label: "Benefits", href: "/benefits" },
    { icon: Clock, label: "Time Off", href: "/pto" },
    { icon: FileText, label: "Documents", href: "/documents" },
    { icon: Shield, label: "ACA Compliance", href: "/aca-compliance" },
    { icon: Users, label: "Company", href: "/company" },
    { icon: BarChart, label: "Reports", href: "/reports" },
  ];
};

// Context for sidebar state
const SidebarContext = createContext<{
  isCollapsed: boolean;
  toggleSidebar: () => void;
  // Direct setter, for pages (e.g. the quoting wizard) that want to default
  // to collapsed without fighting toggleSidebar's flip semantics.
  setCollapsed: (collapsed: boolean) => void;
}>({
  isCollapsed: false,
  toggleSidebar: () => {},
  setCollapsed: () => {},
});

export const useSidebar = () => useContext(SidebarContext);

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <SidebarContext.Provider value={{ isCollapsed, toggleSidebar, setCollapsed: setIsCollapsed }}>
      {children}
    </SidebarContext.Provider>
  );
}

export function Sidebar() {
  const [location] = useLocation();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const { currentRole, user, isAuthenticated } = useRole();
  const [isAgentOrProducer, setIsAgentOrProducer] = useState(false);
  const profileCompletion = useProfileCompletion();
  const { canAny, permissions, isLoading: permissionsLoading } = usePermissions();

  // Check if user is an agent or producer
  // For agency_member users, we need to check their employee record
  const shouldCheckEmployee = isAuthenticated && (currentRole === 'employee' || user?.role === 'agency_member');
  
  const { data: employeeRecord } = useQuery({
    queryKey: ["/api/employees/me"],
    queryFn: async () => {
      const response = await fetch("/api/employees/me");
      if (!response.ok) return null;
      return response.json();
    },
    enabled: shouldCheckEmployee,
  });

  // Get agency ID - for agency_member, use their agencyId, for broker use their userId
  const agencyId = user?.agencyId || (user?.role === 'broker' ? user?.id : null);

  const { data: jobTitles = [] } = useQuery({
    queryKey: ["/api/org/job-titles", "agency", agencyId],
    queryFn: async () => {
      if (!agencyId) return [];
      const response = await fetch(`/api/org/job-titles?ownerType=agency&ownerId=${agencyId}`);
      if (!response.ok) return [];
      return response.json();
    },
    enabled: !!agencyId && !!employeeRecord,
  });

  useEffect(() => {
    // Check the position field directly (case-insensitive)
    if (employeeRecord?.position) {
      const positionLower = employeeRecord.position.toLowerCase();
      if (positionLower === 'producer' || positionLower === 'agent') {
        setIsAgentOrProducer(true);
        return;
      }
    }

    // Check via employee record and job title ID
    if (employeeRecord && jobTitles.length > 0) {
      const producerJobTitleId = jobTitles.find((jt: any) => jt.name === "Producer")?.id;
      const agentJobTitleId = jobTitles.find((jt: any) => jt.name === "Agent")?.id;
      
      const isProducer = employeeRecord.jobTitleId === producerJobTitleId;
      const isAgent = employeeRecord.jobTitleId === agentJobTitleId;
      
      if (isProducer || isAgent) {
        setIsAgentOrProducer(true);
        return;
      }
    }

    setIsAgentOrProducer(false);
  }, [employeeRecord, jobTitles]);

  // Get navigation items based on current role and filter by permissions.
  // Returns NavEntry[] so superadmin can use grouped entries while other
  // portals continue with flat NavItem entries (NavItem ⊂ NavEntry).
  const navigationItems = useMemo<NavEntry[]>(() => {
    let items: NavEntry[];
    switch (currentRole) {
      case 'superadmin':
        items = superadminNavigationEntries;
        break;
      case 'employer':
        items = employerNavigationItems as NavItem[];
        break;
      case 'employee':
        items = getEmployeeNavigationItems(
          !permissionsLoading && canAny(["commissions:view", "commissions:manage", "commissions:reports"])
        ) as NavItem[];
        break;
      case 'ga':
        items = gaNavigationItems;
        break;
      default:
        items = getBrokerNavigationItems();
        break;
    }

    // For broker portal, filter items based on permissions.
    // While permissions are loading: only show items that don't require permissions.
    // This avoids the flash where all items appear then restricted ones disappear.
    // Once permissions load, restricted items fade in additively.
    if (currentRole === 'broker') {
      items = (items as NavItem[]).filter(item => {
        if (!item.permissions || item.permissions.length === 0) return true;
        if (permissionsLoading) return false; // hide until we know
        return canAny(item.permissions);
      });
    }

    return items;
    // Depend on `permissions` (stable reference from usePermissions) rather
    // than `canAny` which is a fresh function each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRole, isAgentOrProducer, permissionsLoading, permissions]);

  // Group open/closed persistence for the superadmin sidebar.
  const groupState = useGroupOpenState();

  return (
    <nav
      className={cn(
        "fixed inset-y-0 left-0 z-40 bg-background shadow-lg pt-16 transition-all duration-300",
        isCollapsed ? "w-16" : "w-64",
      )}
    >
      <div className="h-full overflow-y-auto">
        {/* Toggle Button */}
        <div className="flex justify-end p-2 border-b border-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSidebar}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="h-8 w-8 p-0 text-primary hover:bg-muted"
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Navigation Items — keyed on role so the whole list remounts on role switch */}
        <div className="px-3 py-4">
          <ul key={currentRole} className="space-y-2 font-medium">
            {(() => {
              // Counter used for staggered animation across both groups + items
              let animationIndex = 0;

              // Render a single nav item (extracted so groups + flat lists share it)
              const renderItem = (item: NavItem, opts: { inGroup?: boolean } = {}) => {
                const Icon = item.icon;
                const isActive = location === item.href;
                const isAgencyLink = item.href === "/agency";
                const showProfileWarning = isAgencyLink &&
                  currentRole === 'broker' &&
                  !profileCompletion.isLoading &&
                  !profileCompletion.isComplete;

                const navContent = (
                  <div
                    className={cn(
                      "flex items-center p-2 rounded-lg group transition-colors relative cursor-pointer",
                      isActive
                        ? "text-primary bg-primary/10"
                        : "text-foreground hover:bg-muted hover:text-primary",
                      isCollapsed && "justify-center",
                      // Slight indent when nested under a group header (expanded sidebar only)
                      opts.inGroup && !isCollapsed && "ml-3",
                    )}
                    title={isCollapsed ? item.label : undefined}
                  >
                    <Icon className={cn("w-5 h-5", isCollapsed ? "mx-auto" : "")} />
                    {!isCollapsed && <span className="ml-3 flex-1">{item.label}</span>}

                    {/* Profile completion warning indicator */}
                    {showProfileWarning && !isCollapsed && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center">
                            <AlertCircle className="w-4 h-4 text-amber-500" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          <div className="space-y-1">
                            <p className="font-medium text-amber-600">
                              Profile {profileCompletion.completionPercentage}% complete
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Missing: {profileCompletion.missingFields.slice(0, 3).map(f => f.label).join(", ")}
                              {profileCompletion.missingFields.length > 3 && ` +${profileCompletion.missingFields.length - 3} more`}
                            </p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {showProfileWarning && isCollapsed && (
                      <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-500 rounded-full" />
                    )}

                    {isCollapsed && (
                      <div className="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                        {item.label}
                        {showProfileWarning && (
                          <span className="block text-xs text-amber-300">
                            Profile incomplete ({profileCompletion.completionPercentage}%)
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );

                const baseHref = isAgencyLink && showProfileWarning ? "/agency?tab=profile" : item.href;
                const separator = baseHref.includes("?") ? "&" : "?";
                const linkHref = `${baseHref}${separator}role=${currentRole}`;

                const idx = animationIndex++;
                return (
                  <li
                    key={item.href}
                    data-tour={(item as any).tourId}
                    className="animate-in fade-in slide-in-from-left-1 duration-300"
                    style={{ animationDelay: `${Math.min(idx * 20, 160)}ms`, animationFillMode: 'backwards' }}
                  >
                    <Link href={linkHref}>{navContent}</Link>
                  </li>
                );
              };

              // Render a collapsible group header + its items (superadmin only)
              const renderGroup = (group: NavGroup) => {
                const open = groupState.isOpen(group.id);
                const Icon = group.icon;
                const idx = animationIndex++;

                // When the sidebar is collapsed (narrow), groups can't show their
                // body; render the items inline without a header instead.
                if (isCollapsed) {
                  return (
                    <li key={`group:${group.id}`} className="contents">
                      {group.items.map((item) => renderItem(item))}
                    </li>
                  );
                }

                return (
                  <li
                    key={`group:${group.id}`}
                    className="animate-in fade-in slide-in-from-left-1 duration-300"
                    style={{ animationDelay: `${Math.min(idx * 20, 160)}ms`, animationFillMode: 'backwards' }}
                  >
                    <button
                      type="button"
                      onClick={() => groupState.toggle(group.id)}
                      className="w-full flex items-center p-2 rounded-lg text-foreground hover:bg-muted transition-colors"
                      aria-expanded={open}
                    >
                      <Icon className="w-5 h-5 shrink-0" />
                      <span className="ml-3 flex-1 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {group.label}
                      </span>
                      {open ? (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      )}
                    </button>
                    {open && (
                      <ul className="mt-1 space-y-1">
                        {group.items.map((item) => renderItem(item, { inGroup: true }))}
                      </ul>
                    )}
                  </li>
                );
              };

              return navigationItems.map((entry) =>
                isNavGroup(entry) ? renderGroup(entry) : renderItem(entry)
              );
            })()}
          </ul>
        </div>
      </div>
    </nav>
  );
}
